




import * as d3 from "d3";
import { addDays } from "date-fns";
import crossfilter, { Group } from "crossfilter2";
import { defineComponent, nextTick, ref, watch } from "@vue/composition-api";

import { Document } from "@/types/documents";
import { CodeMatch } from "@/utils/encoding";

interface Item {
  match: CodeMatch;
  document: Document;
}

interface TimelineRecord {
  name: string;
  date: Date;
  raw: Item;
}

interface Props {
  records: TimelineRecord[];
  height: number;
}

export default defineComponent<Props>({
  name: "TimelineSlider",
  props: {
    records: { type: Array, required: true },
    height: { type: Number }
  },
  setup(props, context) {
    const padding = { top: 80, bot: 20, left: 30, right: 15 };

    const root = ref<HTMLDivElement | null>(null);

    const draw = () => {
      if (root.value === null || !props.records || !props.height) {
        return;
      }

      const recordsByDate = crossfilter(props.records).dimension(
        record => record.date
      );

      const xScale = d3
        .scaleLinear()
        .domain([
          new Date(recordsByDate.bottom(1)[0].date),
          addDays(new Date(recordsByDate.top(1)[0].date), 1)
        ])
        .range([padding.left + 10, root.value.clientWidth - padding.right]);

      const groupedDates: Group<
        TimelineRecord,
        Date,
        number
      > = recordsByDate.group();

      const yScale = d3
        .scaleLinear()
        .domain([0, groupedDates.top(1)[0].value as number])
        .range([props.height - padding.bot, 0]);

      const hScale = d3
        .scaleLinear()
        .domain([0, groupedDates.top(1)[0].value as number])
        .range([0, props.height - padding.bot]);

      const canvas = d3
        .select(root.value)
        .html("")
        .append("svg")
        .attr("width", root.value.clientWidth)
        .attr("height", props.height)
        .style("overflow", "visible");

      canvas
        .append("g")
        .attr("transform", `translate(0, ${props.height - padding.bot})`)
        .call(
          d3
            .axisBottom(xScale)
            .tickFormat(d3.timeFormat("%Y %b") as any)
            .ticks(root.value.clientWidth / 60)
        );

      canvas
        .selectAll(".date")
        .data(groupedDates.top(Infinity))
        .enter()
        .append("rect")
        .attr("x", d => xScale && xScale(d.key) - 10)
        .attr("y", d => yScale && yScale(d.value))
        .attr("width", 10)
        .attr("height", d => hScale && hScale(d.value));

      const brush = d3
        .brushX()
        .extent([
          [padding.left, 0],
          [root.value.clientWidth - padding.right, props.height - padding.bot]
        ])
        .on("brush end", () => {
          context.emit(
            "pan",
            d3.event.selection
              .map(xScale.invert, xScale)
              .map((e: number) => new Date(e))
          );
        });

      canvas
        .append("g")
        .attr("class", "brush")
        .call(brush)
        .call(brush.move, [
          xScale(new Date(recordsByDate.bottom(1)[0].date)) - 10,
          xScale(new Date(recordsByDate.top(1)[0].date)) + 1
        ]);
    };

    watch(() => props.records, () => draw());

    nextTick(() => draw());

    return { root };
  }
});
